/**
 * A position in a buffer, so no frame writer carries its own offset arithmetic.
 *
 * Every bug this prevents is the same bug: a field written at the offset the field before it should
 * have used. The writer advances itself and refuses to run past its capacity, the reader refuses to
 * read past what it was given, and both fail loudly rather than returning a zero that looks like a
 * body standing at the origin.
 *
 * Little endian throughout, matching the rest of `wire/`.
 */

import { NetError } from '@/net/session/sessionErrors';

export interface ByteWriter {
  readonly view: DataView;
  /** How many bytes have been written. */
  offset(): number;
  /** How many more will fit. */
  remaining(): number;
  /** Advances by `bytes` and returns the offset to write at. Throws past capacity. */
  claim(bytes: number): number;
  u8(value: number): void;
  u16(value: number): void;
  u32(value: number): void;
  /** A run of bytes, length written by the caller. For text and for anything already encoded. */
  raw(value: Uint8Array): void;
  /** Everything written so far, as a view into the buffer. Copy anything kept. */
  bytes(): Uint8Array;
}

export interface ByteReader {
  readonly view: DataView;
  offset(): number;
  remaining(): number;
  /** Advances by `bytes` and returns the offset to read from. Throws past the end. */
  claim(bytes: number): number;
  u8(): number;
  u16(): number;
  u32(): number;
  /** A run of bytes, as a view into the payload. Copy anything kept past the decode. */
  raw(length: number): Uint8Array;
}

export function createWriter(capacity: number): ByteWriter {
  const buffer = new Uint8Array(new ArrayBuffer(capacity));
  const view = new DataView(buffer.buffer);
  let at = 0;

  const claim = (bytes: number): number => {
    if (at + bytes > capacity) {
      throw new NetError(
        'malformedFrame',
        `writing ${String(bytes)} bytes would pass the ${String(capacity)} byte packet budget`,
      );
    }
    const start = at;
    at += bytes;
    return start;
  };

  return {
    view,
    offset: (): number => at,
    remaining: (): number => capacity - at,
    claim,
    u8: (value: number): void => {
      view.setUint8(claim(1), value);
    },
    u16: (value: number): void => {
      view.setUint16(claim(2), value, true);
    },
    u32: (value: number): void => {
      view.setUint32(claim(4), value, true);
    },
    raw: (value: Uint8Array): void => {
      buffer.set(value, claim(value.byteLength));
    },
    bytes: (): Uint8Array => buffer.subarray(0, at),
  };
}

export function createReader(payload: Uint8Array): ByteReader {
  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  let at = 0;

  const claim = (bytes: number): number => {
    if (at + bytes > payload.byteLength) {
      throw new NetError(
        'malformedFrame',
        `a payload of ${String(payload.byteLength)} bytes ends before ${String(bytes)} more`,
      );
    }
    const start = at;
    at += bytes;
    return start;
  };

  return {
    view,
    offset: (): number => at,
    remaining: (): number => payload.byteLength - at,
    claim,
    u8: (): number => view.getUint8(claim(1)),
    u16: (): number => view.getUint16(claim(2), true),
    u32: (): number => view.getUint32(claim(4), true),
    raw: (length: number): Uint8Array => payload.subarray(claim(length), at),
  };
}
